import makeStyles from '@mui/styles/makeStyles'
import Typography from '@mui/material/Typography'
import BillboardAd from 'components/BillboardAd'
import Layout from 'components/Layout'
import SwiperShows from 'components/SwiperShows'
import { RANGE_2DAYS, RANGE_7DAYS, SORT_CLICKS, SORT_EXPIRYDATE, SORT_RANKING, TITLE_EXPIRING, TITLE_JUST_ADDED, TITLE_TOP20_2DAYS, TITLE_TOP20_7DAYS } from 'lib/constants'
import { getShows, getShowsForStation } from 'lib/shows'
import { getStation, getStations, isJoynStation, isTVNowStation } from 'lib/stations'
import { getImageURL, sortByRemainingHours } from 'lib/utils'
import Head from 'next/head'

const useStyles = makeStyles((theme) => ({
  headerWrapper: {
    width: '100%'
  },
  adContainer: {
    marginTop: -theme.spacing(3),
    marginBottom: theme.spacing(7)
  },
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  },
  logo: {
    width: '100px',
    flex: '0 0 100px'
  },
  title: {
    flex: '1 1 auto',
    textAlign: 'left'
  },
  '@global': {
    '.ad-stations-leaderboard': {
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('md')]: {
        width: '320px',
        height: '100px'
      },
      [theme.breakpoints.up('md')]: {
        width: '728px',
        height: '90px'
      }
    }
  }
}))

export default function Station ({ station, top20days7, top20days2, series, movies, dokus, comedy, justFound, expiring }) {
  const classes = useStyles()

  const description = (station) => {
    if (isTVNowStation(station.slug)) {
      return `Alle Videos, Filme und Serien von ${station.name} (TVNOW) und den anderen TV-Sendern sofort finden und kostenlos online abrufen - einfach, schnell und unkompliziert`
    } else if (isJoynStation(station.slug)) {
      return `Alle Videos, Filme und Serien von ${station.name} (Joyn) und den anderen TV-Sendern sofort finden und kostenlos online abrufen - einfach, schnell und unkompliziert`
    } else {
      return `Alle Videos, Filme und Serien von ${station.name} und den anderen TV-Sendern sofort finden und kostenlos online abrufen - einfach, schnell und unkompliziert`
    }
  }

  const title = (station) => {
    return `${station.name} Mediathek - Sendung verpasst?`
  }

  let lcpImage = null
  if (top20days7.length > 0) {
    const firstItem = top20days7[0]._source
    lcpImage = getImageURL(firstItem.image, firstItem.stations[0].slug)
  }
  return (
    <>
      <Head>
        <title>{title(station)}</title>
        <meta name='description' content={description(station)} />
        {lcpImage &&
          <link rel='preload' as='image' href={lcpImage} />}
      </Head>
      <Layout header={
        <div className={classes.headerWrapper}>
          <div className={classes.header}>
            <Typography component='h1' variant='h1' className={classes.title}>
              Videos der {station.name} Mediathek
            </Typography>
            <img src={station.logoWhite} alt={station.name} title={station.name} className={classes.logo} width={100} height={56} />
          </div>
        </div>
        }
      >
        <div className={classes.adContainer}>
          <BillboardAd slot={process.env.NEXT_PUBLIC_ADSENSE_SLOT_STATIONS_LEADERBOARD} />
        </div>

        <SwiperShows title={TITLE_TOP20_7DAYS} items={top20days7} counter clicks href={'/search?sort=clicks&station=' + station.slug} />
        <SwiperShows title={TITLE_TOP20_2DAYS} items={top20days2} clicks href={'/search?sort=clicks&station=' + station.slug} />
        <SwiperShows title='Serien' items={series} href={'/search?sort=clicks&station=' + station.slug + '&cat=serien'} />
        <SwiperShows title='Filme' items={movies} href={'/search?sort=clicks&station=' + station.slug + '&cat=filme'} />
        <SwiperShows title='Dokus' items={dokus} href={'/search?sort=clicks&station=' + station.slug + '&cat=dokus'} />
        <SwiperShows title='Comedy und Shows' items={comedy} href={'/search?sort=clicks&station=' + station.slug + '&cat=comedyshows'} />
        <SwiperShows title={TITLE_JUST_ADDED} items={justFound} href={'/search?sort=date&station=' + station.slug} />
        <SwiperShows title={TITLE_EXPIRING} items={expiring} expiryDate />

      </Layout>
    </>
  )
}

export async function getStaticPaths () {
  const paths = getStations().map(station => ({
    params: { station: station.slug }
  }))

  return {
    paths,
    fallback: false
  }
}

export async function getStaticProps ({ params }) {
  const slug = params.station
  // getServerSideProps requires JSON object
  let station = getStation(slug)

  if (!station) {
    return { notFound: true }
  }
  station = station.toJSON()
  const top20days7Result = await getShowsForStation(slug, RANGE_7DAYS)
  const top20days2Result = await getShows({ sort: SORT_CLICKS, station: station.slug, range: RANGE_2DAYS })
  const seriesResult = await getShows({ category: 'serien', station: station.slug, sort: SORT_CLICKS, range: RANGE_7DAYS })
  const moviesResult = await getShows({ category: 'filme', station: station.slug, sort: SORT_CLICKS, range: RANGE_7DAYS })
  const dokusResult = await getShows({ category: 'dokus', station: station.slug, sort: SORT_CLICKS, range: RANGE_7DAYS })
  const comedyResult = await getShows({ category: 'comedyshows', station: station.slug, sort: SORT_CLICKS, range: RANGE_7DAYS })
  const justFoundResult = await getShows({ sort: SORT_RANKING, station: station.slug })
  const expiringResult = await getShows({ sort: SORT_EXPIRYDATE, station: station.slug, trailer: false })

  const top20days7 = top20days7Result.hits.hits
  const top20days2 = top20days2Result.hits.hits
  const series = seriesResult.hits.hits
  const movies = moviesResult.hits.hits
  const dokus = dokusResult.hits.hits
  const comedy = comedyResult.hits.hits
  const justFound = justFoundResult.hits.hits
  const expiring = sortByRemainingHours(expiringResult.hits.hits)

  return {
    props: {
      station,
      top20days7,
      top20days2,
      series,
      movies,
      dokus,
      comedy,
      justFound,
      expiring
    },
    revalidate: parseInt(process.env.CACHE_STATION)
  }
}
